/**
 * FogBugz Formulator constants (epic PI-SPEC-SW-E309, ER-1665 Package C,
 * contract fogbugz-formulator 1).
 *
 * Single source of truth for the limits the SpecTree API, the SpecTree
 * Toolbox fogbugz-formulator engine, the internal plugin skill
 * (`toolbox-fogbugz-formulator`) and the Toolbox page share. Import these;
 * never re-type the numbers. Browser-safe: no runtime dependencies (the zod
 * schemas that use these live in `schemas.ts`).
 *
 * The contract package depends on zod only, so the few values that also live
 * in `@spectree/shared` (the Intent v1 kinds, source and slot ids, the EVTX
 * caps) are re-declared here; `packages/shared` tests prove they are equal.
 */
/**
 * The marker the internal skill prints at the start of its one result line:
 * `SPECTREE_FOGBUGZ_FORMULATOR_TURN {json}`. The JSON parses with
 * `fogbugzFormulatorTurnMarkerSchema`; the engine posts it (minus
 * `sessionId`) to SpecTree as the engine turn
 * (`fogbugzFormulatorTurnBodySchema`).
 */
export const FOGBUGZ_FORMULATOR_TURN_MARKER = "SPECTREE_FOGBUGZ_FORMULATOR_TURN";
/** Version of the `turn.json` file the engine writes for the skill. */
export const FOGBUGZ_FORMULATOR_TURN_INPUT_SCHEMA_VERSION = 1;
/**
 * How long the opening turn may run before the engine stops it with
 * `TIMED_OUT`: Claude reads the product code first. The same budget as the
 * ADO Formulator engine's opening turn.
 */
export const FOGBUGZ_FORMULATOR_OPENING_TURN_TIMEOUT_MS = 15 * 60_000;
/** How long a reply turn may run before the engine stops it with `TIMED_OUT` (same as the ADO Formulator). */
export const FOGBUGZ_FORMULATOR_REPLY_TURN_TIMEOUT_MS = 6 * 60_000;
/**
 * What the person is filing: something broken (`bug`), a new or changed
 * behaviour (`feature`) or a question (`inquiry`). The same values, in the
 * same order, as Intent v1's `INTENT_KINDS` (`packages/shared/src/intent/types.ts`).
 */
export const FOGBUGZ_FORMULATOR_KINDS = ["bug", "feature", "inquiry"];
/**
 * The kind a chosen FogBugz category decides: `Bug`, `Feature` or `Inquiry`
 * (trimmed, any case), as on the website FogBugz Formulator. Null for no
 * category or any other one: then the person says what they are filing.
 * SpecTree applies this at start and the page skips its kind question when
 * it is not null, so a case is never filed in one category and interviewed
 * (and checked against the Bug bar, or not) as another.
 */
export function fogbugzFormulatorKindForCategory(sCategory) {
    switch (sCategory?.trim().toLowerCase()) {
        case "bug":
            return "bug";
        case "feature":
            return "feature";
        case "inquiry":
            return "inquiry";
        default:
            return null;
    }
}
/**
 * The `source` front-matter value of an `intent.md` the Toolbox FogBugz
 * Formulator writes. One of Intent v1's `INTENT_SOURCES`; the website
 * Formulator keeps `fogbugz-web-formulator`.
 */
export const FOGBUGZ_FORMULATOR_INTENT_SOURCE = "fogbugz-toolbox-formulator";
/**
 * Every Intent v1 slot id, in playbook order: the same list as `SLOT_IDS` in
 * `packages/shared/src/intent/types.ts` (a superset across all kinds; which
 * slots a kind owes is `KIND_SLOT_SETS` there). Re-declared so the contract
 * stays zod-only; a `packages/shared` test proves the two lists are equal.
 */
export const FOGBUGZ_FORMULATOR_SLOT_IDS = [
    "title",
    "symptom",
    "reproSteps",
    "expectedVsActual",
    "question",
    "questionContext",
    "desiredOutcome",
    "proposedDirection",
    "successSignal",
    "usersAffected",
    "frequency",
    "environment",
    "whoBenefits",
    "answerAudience",
    "severity",
    "reproducibility",
    "workaround",
    "currentWorkaround",
    "answerByWhen",
    "boundary",
];
// ---------------------------------------------------------------------------
// Attachments (kept on the person's computer until submit)
// ---------------------------------------------------------------------------
/**
 * Largest screenshot the Formulator takes, in bytes (10 MB): the website
 * Formulator's screenshot route limit (`POST /api/v1/fogbugz/interview/:id/screenshot`).
 */
export const FOGBUGZ_FORMULATOR_SCREENSHOT_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Most screenshots one session keeps. The website Formulator has no count
 * limit; the Toolbox keeps files in its private turn folder and uploads them
 * all in one multipart submit, so the count is bounded here.
 */
export const FOGBUGZ_FORMULATOR_MAX_SCREENSHOTS = 10;
/** Image types a screenshot may have (images only, as on the website). */
export const FOGBUGZ_FORMULATOR_IMAGE_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
/**
 * Largest `.evtx` file the Formulator takes, in bytes (10 MiB): the same value
 * as `EVTX_MAX_BYTES` in `packages/shared/src/evtx/constants.ts`.
 */
export const FOGBUGZ_FORMULATOR_EVTX_MAX_BYTES = 10 * 1024 * 1024;
/**
 * Most `.evtx` files one session keeps: the same value as
 * `MAX_EVENT_LOGS_PER_SESSION` in `packages/shared/src/evtx/constants.ts`.
 */
export const FOGBUGZ_FORMULATOR_MAX_EVENT_LOGS = 3;
/** Longest attachment file name (screenshot or event log). */
export const FOGBUGZ_FORMULATOR_ATTACHMENT_NAME_MAX = 200;
// ---------------------------------------------------------------------------
// Turn caps
// ---------------------------------------------------------------------------
/** Longest reply accepted in one turn (same as the ADO Formulator). */
export const FOGBUGZ_FORMULATOR_REPLY_MAX = 8000;
/** Longest value Claude may extract for one slot. */
export const FOGBUGZ_FORMULATOR_SLOT_VALUE_MAX = 4000;
/** Longest provenance pointer on an extracted slot value (`code: src/x.ts`, `screenshot 1`, ...). */
export const FOGBUGZ_FORMULATOR_SLOT_SOURCE_MAX = 300;
/** Longest question Claude may ask in one turn. */
export const FOGBUGZ_FORMULATOR_QUESTION_MAX = 1000;
/** Most "What Claude found in the code" entries one turn may carry. */
export const FOGBUGZ_FORMULATOR_CODE_FINDINGS_MAX = 12;
/** Longest repository-relative path of a code finding. */
export const FOGBUGZ_FORMULATOR_CODE_FINDING_PATH_MAX = 512;
/** Longest plain note of a code finding. */
export const FOGBUGZ_FORMULATOR_CODE_FINDING_NOTE_MAX = 500;
/** Most transcript messages the engine hands the skill in `turn.json`. */
export const FOGBUGZ_FORMULATOR_TRANSCRIPT_MAX = 12;
/** Longest `intent.md` the review step and the submit carry (same as the website review cap). */
export const FOGBUGZ_FORMULATOR_INTENT_MAX_CHARS = 100_000;
/** Most open cases the review step lists as possible duplicates. */
export const FOGBUGZ_FORMULATOR_DUPLICATES_MAX = 10;
/** Longest duplicate excerpt (the website's `DEDUPE_SNIPPET_MAX_CHARS`). */
export const FOGBUGZ_FORMULATOR_DUPLICATE_SNIPPET_MAX = 240;
/**
 * Whose move it is: `claude` (the engine owes the next turn), `person` (a
 * question waits for an answer) or `review` (coverage is complete; the person
 * reviews `intent.md` and the Bug bar before Submit).
 */
export const FOGBUGZ_FORMULATOR_AWAITING = ["claude", "person", "review"];
/** `opening` for the first turn (no answer yet), `reply` for every later turn. */
export const FOGBUGZ_FORMULATOR_TURN_MODES = ["opening", "reply"];
/** Who wrote a transcript message. */
export const FOGBUGZ_FORMULATOR_MESSAGE_ROLES = ["assistant", "user"];
/**
 * Where a Bug-bar miss comes from: Intent v1 coverage (a required bug slot
 * not filled) or the ADO grooming bar for a Bug
 * (`packages/api/src/config/readiness-rubric.v1.json`).
 */
export const FOGBUGZ_BUG_BAR_SOURCES = ["intent", "grooming-bar"];
//# sourceMappingURL=constants.js.map